/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import CloseIcon from '@material-ui/icons/Close'
import useStyles from 'components/Dialog/Product/style'
import {
  DETAIL_MANUAL_PRODUCT_HEADER,
  PREPAID_MANUAL_PRODUCT_HEADER,
} from 'components/Dialog/Product/constants'
import {ProductAvailableType} from 'api/productApi'
import useProductManagement from 'features/productManagement/useProductManagement'

import {
  OFFER_TYPE,
  OffersType,
  PAYMENT_METHOD,
  PaymentMethod,
} from 'pages/ProductsPages/constant'
import {formatPriceNumber} from 'helpers/uiHelper'
import useFailureModal from 'features/modal/useFailureModal'
import {PACKAGE_AVAILABLE_IMAGE_ID} from 'pages/ProductOrderPages/constants'
import {useAppDispatch} from 'hooks'
import {
  openReportImageDialog,
  setReportDialogId,
} from 'features/modal/modalSlice'

interface DetailProductOrderDialogProps {
  isOpen: boolean
  typeCharge: PaymentMethod
  productName: string
  paymentMethod: string
  totalAmount: string
  totalOriginalAmount: string
  offerType: OffersType
  duration: number
  description: string
  onClose: () => void
  goods: (ProductAvailableType['offer']['offerItems'][number] & {
    usageAmount: number
    chargeAmount: number
    order: number
  })[]
  onBuyProduct?: () => void
  canBuyProduct?: boolean
}

const DetailProductOrderDialog = ({
  isOpen,
  onClose,
  typeCharge,
  productName,
  paymentMethod,
  totalAmount,
  totalOriginalAmount,
  offerType,
  duration,
  description,
  goods,
  onBuyProduct,
  canBuyProduct = false,
}: DetailProductOrderDialogProps) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const [isReadTerms, setIsReadTerms] = useState<boolean>(false)
  const {listPackage, onSetIsAgreeToPurchase} = useProductManagement()
  const ref = useRef<HTMLDivElement>(null)
  const {onOpen: onOpenFailureModal} = useFailureModal()

  const listHeader = useMemo(() => {
    if (typeCharge !== PAYMENT_METHOD.MANUAL) {
      return [...DETAIL_MANUAL_PRODUCT_HEADER, ...PREPAID_MANUAL_PRODUCT_HEADER]
    }
    return DETAIL_MANUAL_PRODUCT_HEADER
  }, [i18n.language, typeCharge])

  const getCLS = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    if (item.length) {
      return item[0].cls
    }
    return ''
  }

  const getPackageName = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    return (
      item[0]?.displayInfos?.find(
        (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  const data = useMemo(() => {
    const value = goods
      .map((item) => {
        return {
          ...item,
          cls: getCLS(item.pkgItemId),
          pkgName: getPackageName(item.pkgItemId),
        }
      })
      .toSorted((a, b) => a.cls.localeCompare(b.cls) || a.order - b.order)

    return value
  }, [goods])

  const rowSpanMap = data.reduce<Record<string, number>>((acc, curr) => {
    acc[curr.cls] = (acc[curr.cls] || 0) + 1
    return acc
  }, {})
  const seenCls = new Set<string>()

  const dispatch = useAppDispatch()

  const handleClose = () => {
    onClose()
  }

  const contentGuide = useMemo(() => {
    if (typeCharge === PAYMENT_METHOD.MANUAL) {
      return t('IUserGuideDescSettlement')
    }
    if (typeCharge === PAYMENT_METHOD.PREPAYMENT) {
      if (offerType === OFFER_TYPE.ONCE) {
        return t('IUserGuideDescBulk')
      }
      if (offerType === OFFER_TYPE.MONTHLY) {
        return t('IUserGuideDescMonthly')
      }
    }
    return ''
  }, [typeCharge, i18n.language])

  const disabledButton = useMemo(() => {
    return !goods.some((item) => item.offerAmount > 0)
  }, [])

  const showReportImage = (packageId: string) => {
    if (PACKAGE_AVAILABLE_IMAGE_ID.includes(packageId)) {
      dispatch(setReportDialogId(packageId))
      dispatch(openReportImageDialog())
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (ref.current && isOpen) {
        ref.current.innerHTML = contentGuide
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [i18n.language, isOpen])

  const onPurchase = () => {
    if (!isReadTerms) {
      onOpenFailureModal(t('IAgreeToTermsBeforePurchase'))
    } else if (onBuyProduct) {
      onSetIsAgreeToPurchase(true)
      onBuyProduct()
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className={classes.container}>
      <div className={classes.closeButtonWrap}>
        <IconButton
          color='secondary'
          aria-label='favorite'
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <DialogContent className={classes.contentWrap}>
        <div className={classes.headerDialog}>
          <div className={classes.productInfo}>
            <div className={classes.headerTitle}>
              <h2 className={classes.productTitle}>{productName}</h2>
              {canBuyProduct && (
                <Button
                  className={classes.purchaseButton}
                  color='primary'
                  variant='contained'
                  onClick={onPurchase}
                  disabled={disabledButton}
                >
                  {t('IBuyV1')}
                </Button>
              )}
            </div>
            <p className={classes.productType}>{paymentMethod}</p>

            <div className={classes.price}>
              {totalAmount === totalOriginalAmount ? (
                <div>{totalAmount}</div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div>{totalAmount}</div>
                  <div
                    style={{
                      textDecoration: 'line-through',
                      opacity: 0.5,
                    }}
                  >
                    {totalOriginalAmount}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={classes.periodSection}>
          <div className={classes.periodText}>
            {t('IDurationV2')} :{' '}
            <span className={classes.periodHighlight}>
              {duration > 10000000
                ? t('IUnlimited')
                : `${duration} ${t('IMonths')}`}
            </span>
            <br />
            {t('IDescription')} :{' '}
            <span className={classes.description}>{description}</span>
          </div>
        </div>
        <Table
          size='small'
          className={classes.table}
          style={{tableLayout: 'fixed'}}
        >
          <TableHead className={classes.tableHeader}>
            {listHeader.map((item) => (
              <TableCell
                align='center'
                padding='checkbox'
                key={item.id}
                style={{
                  whiteSpace: 'pre',
                }}
                className={classes.headerMax}
              >
                {t(item.label)}
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            {data.map((item) => {
              const isFirstOccurCls = !seenCls.has(item.cls)
              if (isFirstOccurCls) seenCls.add(item.cls)
              return (
                <TableRow key={item.pkgItemId}>
                  {isFirstOccurCls && (
                    <TableCell
                      align='center'
                      padding='checkbox'
                      rowSpan={rowSpanMap[item.cls]}
                      className={classes.headerMax}
                    >
                      {item.cls}
                    </TableCell>
                  )}
                  <TableCell
                    align='center'
                    padding='checkbox'
                    className={classes.headerMax}
                  >
                    <div
                      className={
                        PACKAGE_AVAILABLE_IMAGE_ID.includes(item.pkgItemId)
                          ? classes.clickableText
                          : ''
                      }
                      onClick={() => showReportImage(item.pkgItemId)}
                    >
                      {item.pkgName}
                    </div>
                  </TableCell>
                  {typeCharge !== PAYMENT_METHOD.MANUAL && (
                    <>
                      <TableCell
                        align='center'
                        padding='checkbox'
                        className={classes.headerMax}
                      >
                        {item.offerAmount > 10000000
                          ? t('IUnlimited')
                          : `${formatPriceNumber(
                              item.offerAmount + item.chargeAmount,
                            )} ${
                              item.offerUnit === 'Count'
                                ? t('ICase')
                                : t('IMinute')
                            }`}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <div className={classes.userGuide}>
          <span>{t('ITermsOfUse')}</span>
          {canBuyProduct && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              (
              <Checkbox
                checked={isReadTerms}
                onChange={() => setIsReadTerms(!isReadTerms)}
              />
              <div className={classes.readTermsText}>{t('IIAgreeToTerms')}</div>
              )
            </div>
          )}
        </div>
        <div ref={ref} />
      </DialogContent>
    </Dialog>
  )
}

export default DetailProductOrderDialog
